import {
  Children,
  isValidElement,
  ReactElement,
  ReactNode,
  useContext,
} from "react";
import { useAtom } from "jotai";
import { atomWithStorage, createJSONStorage } from "jotai/utils";
import { t } from "@lingui/core/macro";

import { Icon } from "_/components/icon";
import { Tooltip } from "_/components/tooltip";
import { type DimensionVariant, type ContentDimension } from "_/data/docs";
import { docsUrls } from "_/routes";

import { DocsContext } from "./context";

import * as S from "./styled";

type TabProps = {
  // The variants this tab covers, comma-separated. Each entry is either a single
  // value (e.g. `2`) or an open-ended range anchored at one: `2-` covers v2 and
  // every later variant, `-2` covers v2 and every earlier one. There is still no
  // wildcard or catch-all form, so a variant no tab lists is simply not covered
  // by this block.
  appliesTo: string;
  // Tab button text. Used only when the `<Tab>`s define the domain themselves;
  // a declared dimension takes its labels from the manifest, since one `<Tab>`
  // does not own a whole variant's button.
  label?: string;
  children: ReactNode;
};

/**
 * Persisted selection for one content dimension, backed by its own localStorage
 * entry under `docsTabs:<kind>` — namespaced so a `kind` cannot collide with an
 * unrelated key elsewhere in the app.
 */
const createTabSelectionAtom = (kind: string) =>
  atomWithStorage<string | null>(
    `docsTabs:${kind}`,
    null,
    createJSONStorage(() => window.localStorage),
    { getOnInit: true }
  );

const tabSelectionAtoms = new Map<
  string,
  ReturnType<typeof createTabSelectionAtom>
>();

/**
 * The selection atom for a `kind`, created once and then reused. Every `<Tabs>`
 * sharing a `kind` has to read and write the *same* atom for the choice to stay
 * in sync across docs blocks and survive navigation and reloads, so the atoms
 * are memoized here rather than rebuilt per render.
 */
const tabSelectionAtom = (kind: string) => {
  const existing = tabSelectionAtoms.get(kind);

  if (existing !== undefined) {
    return existing;
  }

  const created = createTabSelectionAtom(kind);
  tabSelectionAtoms.set(kind, created);

  return created;
};

/**
 * A single tab. On its own it simply renders its children; it is primarily a
 * data holder whose props are read by the parent `<Tabs>` to build the switcher
 * and to resolve which tab covers the selected variant.
 */
export const Tab = ({ children }: TabProps): ReactElement => <>{children}</>;

/**
 * One parsed `appliesTo` entry. `from` is the anchor and every later variant,
 * `to` is the anchor and every earlier one; both are resolved against the
 * declared dimension's `values`, which are ordered oldest to newest.
 */
type CoverageEntry = {
  bound: "exact" | "from" | "to";
  value: string;
};

/**
 * The raw `appliesTo` entries of a tab. `appliesTo` is required, but docs are
 * authored in a separate repo and evaluated at runtime, so an omitted prop has
 * to degrade to "covers nothing" rather than throw and blank the page.
 */
const coverageList = (tab: ReactElement<TabProps>): string[] =>
  (tab.props.appliesTo ?? "")
    .split(",")
    .map((value) => value.trim())
    .filter((value) => value.length > 0);

/**
 * Parses one `appliesTo` entry. Only a leading or trailing `-` opens a range, so
 * a variant value that itself contains a hyphen (e.g. `hylo-2`) is still read as
 * a plain value. An entry marked at both ends (`-2-`, or a bare `-`) has no
 * single anchor, and a closed range is deliberately not a form: `1-3` cannot be
 * told apart from a value spelled with a hyphen.
 */
const parseEntry = (entry: string): CoverageEntry | undefined => {
  const opensLater = entry.endsWith("-");
  const opensEarlier = entry.startsWith("-");

  if (opensLater && opensEarlier) {
    return undefined;
  }

  if (opensLater) {
    return { bound: "from", value: entry.slice(0, -1).trim() };
  }

  if (opensEarlier) {
    return { bound: "to", value: entry.slice(1).trim() };
  }

  return { bound: "exact", value: entry };
};

/** The parsed entries of a tab, with any malformed ones dropped. */
const parsedEntries = (tab: ReactElement<TabProps>): CoverageEntry[] =>
  coverageList(tab)
    .map(parseEntry)
    .filter((entry): entry is CoverageEntry => entry !== undefined);

/**
 * The variant values one entry covers, given the domain in oldest-to-newest
 * order. A range is enumerated from the anchor's position, so it follows the
 * order the dimension declares rather than any numeric reading of the values.
 * An anchor the domain does not contain covers nothing.
 */
const resolveEntry = (
  entry: CoverageEntry,
  domain: DimensionVariant[]
): string[] => {
  if (entry.bound === "exact") {
    return [entry.value];
  }

  const values = domain.map((variant) => variant.value);
  const anchor = values.indexOf(entry.value);

  if (anchor === -1) {
    return [];
  }

  return entry.bound === "from"
    ? values.slice(anchor)
    : values.slice(0, anchor + 1);
};

/**
 * The variant values a tab covers. Ranges need a declared dimension to
 * enumerate against, so in an inline block every entry degrades to its own
 * anchor; `reportCoverageErrors` reports that separately.
 */
const coveredValues = (
  tab: ReactElement<TabProps>,
  domain: DimensionVariant[],
  isDeclaredDimension: boolean
): string[] =>
  isDeclaredDimension
    ? parsedEntries(tab).flatMap((entry) => resolveEntry(entry, domain))
    : parsedEntries(tab).map((entry) => entry.value);

/**
 * The domain for a block whose `kind` is not a declared dimension: the anchors
 * its `<Tab>`s name, in document order. Every variant is covered by
 * construction, so such a block never crosses a tab out.
 */
const inlineDomain = (tabs: ReactElement<TabProps>[]): DimensionVariant[] => {
  const variants = new Map<string, DimensionVariant>();

  for (const tab of tabs) {
    const values = parsedEntries(tab).map((entry) => entry.value);
    // `label` names one button, so it only applies to a tab covering a single
    // variant; with no declared dimension there is nowhere else to get labels.
    const label = values.length === 1 ? tab.props.label : undefined;

    for (const value of values) {
      if (!variants.has(value)) {
        variants.set(value, { value, label: label ?? value });
      }
    }
  }

  return [...variants.values()];
};

/**
 * Logs the authoring mistakes explicit coverage allows, all silent otherwise: an
 * entry that is neither a value nor a range, a range in a block with no declared
 * order to enumerate, a value the dimension does not declare, and a value
 * claimed by two tabs, which quietly resolves to whichever comes first.
 */
const reportCoverageErrors = (params: {
  kind: string;
  tabs: ReactElement<TabProps>[];
  domain: DimensionVariant[];
  isDeclaredDimension: boolean;
}) => {
  const { kind, tabs, domain, isDeclaredDimension } = params;
  const entries = tabs.flatMap(coverageList);
  const parsed = tabs.flatMap(parsedEntries);
  const declared = new Set(domain.map((variant) => variant.value));

  const malformed = entries.filter((entry) => parseEntry(entry) === undefined);

  if (malformed.length > 0) {
    console.error(
      `<Tabs kind="${kind}">: appliesTo entries ${malformed.join(", ")} are ` +
        "neither a value, a `value-` range nor a `-value` range."
    );
  }

  const ranges = parsed.filter((entry) => entry.bound !== "exact");

  if (ranges.length > 0 && !isDeclaredDimension) {
    console.error(
      `<Tabs kind="${kind}">: appliesTo ranges need the declared order of a ` +
        `content dimension to enumerate, but "${kind}" declares none; each ` +
        "range covers only the variant it names."
    );
  }

  // Only a declared dimension has a domain of its own to contradict; an inline
  // domain is built from these anchors, so none of them can be unknown.
  const unknown = isDeclaredDimension
    ? parsed
        .filter((entry) => !declared.has(entry.value))
        .map((entry) => entry.value)
    : [];

  if (unknown.length > 0) {
    console.error(
      `<Tabs kind="${kind}">: appliesTo names ${unknown.join(", ")}, which ` +
        `the "${kind}" dimension does not declare.`
    );
  }

  const claimed = tabs.flatMap((tab) =>
    coveredValues(tab, domain, isDeclaredDimension)
  );

  const duplicated = new Set(
    claimed.filter((value, index) => claimed.indexOf(value) !== index)
  );

  if (duplicated.size > 0) {
    console.warn(
      `<Tabs kind="${kind}">: ${[...duplicated].join(", ")} claimed by more ` +
        "than one tab; the first in document order wins."
    );
  }
};

/**
 * Tabbed docs section. Each child must be a `<Tab>` naming the variants it
 * covers, either one by one or as an open-ended range (`2-`, `-2`). When `kind`
 * names a declared content dimension (`manifest.contentDimensions`) the bar
 * shows every variant of that dimension and crosses out the ones this block has
 * no tab for; otherwise the `<Tab>`s define the domain between them. The
 * selection is shared across every `<Tabs>` of the same `kind`.
 */
export const Tabs = ({
  kind,
  children,
}: {
  kind: string;
  children: ReactNode;
}): ReactElement => {
  const { manifest } = useContext(DocsContext);
  const [stored, setSelected] = useAtom(tabSelectionAtom(kind));

  const tabs = Children.toArray(children).filter(
    (child): child is ReactElement<TabProps> =>
      isValidElement(child) && child.type === Tab
  );

  if (tabs.length === 0) {
    console.error("<Tabs> expects one or more <Tab> children.");
    return <>{children}</>;
  }

  // A `Record` lookup is typed as always-present, so name the result
  // explicitly: `kind` need not be a declared dimension.
  const dimension: ContentDimension | undefined =
    manifest.contentDimensions[kind];
  const isDeclaredDimension = dimension !== undefined;
  const domain = dimension?.values ?? inlineDomain(tabs);

  reportCoverageErrors({ kind, tabs, domain, isDeclaredDimension });

  if (domain.length === 0) {
    console.error(
      `<Tabs kind="${kind}">: nothing to show. A declared dimension needs ` +
        "values; otherwise every <Tab> needs an appliesTo."
    );
    return <></>;
  }

  const variants = new Map(domain.map((variant) => [variant.value, variant]));
  const coverage = tabs.map((tab) => ({
    tab,
    values: coveredValues(tab, domain, isDeclaredDimension),
  }));
  const covered = new Set(coverage.flatMap((entry) => entry.values));

  // The dimension's default, ignored if it names a variant outside its domain.
  const fallback =
    dimension !== undefined && variants.has(dimension.default)
      ? dimension.default
      : domain[0].value;

  const selected = stored !== null && variants.has(stored) ? stored : fallback;
  const selectedLabel = variants.get(selected)?.label ?? selected;
  const activeTab = coverage.find((entry) =>
    entry.values.includes(selected)
  )?.tab;

  // A single-variant domain has nothing to switch between.
  if (domain.length === 1) {
    return (
      <S.TabsWrapper>
        <S.TabPanel role="tabpanel">{activeTab?.props.children}</S.TabPanel>
      </S.TabsWrapper>
    );
  }

  return (
    <S.TabsWrapper>
      <S.TabBar role="tablist">
        {domain.map((variant) => (
          <S.TabButton
            key={variant.value}
            type="button"
            role="tab"
            aria-selected={variant.value === selected}
            title={
              covered.has(variant.value)
                ? undefined
                : t`docs-view.tab-not-covered ${variant.label}`
            }
            onClick={() => setSelected(variant.value)}
            $active={variant.value === selected}
            $crossedOut={!covered.has(variant.value)}
          >
            {variant.label}
          </S.TabButton>
        ))}
        {dimension && (
          <S.TabBarHelp>
            <Tooltip
              content={dimension.help.label}
              placement="bottomEnd"
              target={
                <a
                  href={`${docsUrls.index}${dimension.help.href}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Icon variant="Help" label={dimension.help.label} />
                </a>
              }
            />
          </S.TabBarHelp>
        )}
      </S.TabBar>
      <S.TabPrintLabel>{selectedLabel}</S.TabPrintLabel>
      <S.TabPanel role="tabpanel">
        {activeTab ? (
          activeTab.props.children
        ) : (
          <S.TabNotApplicable>
            {t`docs-view.tab-not-applicable ${selectedLabel}`}
          </S.TabNotApplicable>
        )}
      </S.TabPanel>
    </S.TabsWrapper>
  );
};
